import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type {
  AcceptSuggestedCheckRequest,
  AnalyzeHarRequest,
  AnalyzeHarResponse,
  BatchAcceptResponse,
  BatchDismissResponse,
  BatchSuggestedCheckRequest,
  Check,
  SuggestedCheck,
  SuggestedChecksResponse,
} from "../types";
import { useApiMutation, useApiQuery } from "./index";

interface UseSuggestedChecksParams {
  app_config_id?: string;
  test_id?: string;
  status?: string;
  method?: string;
  path?: string;
  limit?: number;
  offset?: number;
}

export const useSuggestedChecks = (params?: UseSuggestedChecksParams) => {
  const queryParams = new URLSearchParams();
  if (params?.app_config_id) queryParams.set("app_config_id", params.app_config_id);
  if (params?.test_id) queryParams.set("test_id", params.test_id);
  if (params?.status) queryParams.set("status", params.status);
  if (params?.method) queryParams.set("method", params.method);
  if (params?.path) queryParams.set("path", params.path);
  if (params?.limit) queryParams.set("limit", params.limit.toString());
  if (params?.offset) queryParams.set("offset", params.offset.toString());

  const queryString = queryParams.toString();
  const url = queryString ? `/checks/suggested?${queryString}` : "/checks/suggested";

  return useApiQuery<SuggestedChecksResponse>(url);
};

export const useSuggestedCheck = (suggestionId: string) => {
  return useApiQuery<SuggestedCheck>(`/checks/suggested/${suggestionId}`, {
    enabled: !!suggestionId,
  });
};

export const useAnalyzeHar = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useApiMutation<AnalyzeHarRequest, AnalyzeHarResponse>(
    "POST",
    "/checks/analyze-har/{test_run_id}",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["/checks/suggested"] });
        enqueueSnackbar("HAR analysis started", {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to analyze HAR: ${error.message}`, {
          variant: "error",
        });
      },
    },
  );
};

export const useAnalyzeHarSync = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useApiMutation<AnalyzeHarRequest, SuggestedChecksResponse>(
    "POST",
    "/checks/analyze-har/{test_run_id}/sync",
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["/checks/suggested"] });
        enqueueSnackbar(`Found ${data.suggestions.length} API calls`, {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to analyze HAR: ${error.message}`, {
          variant: "error",
        });
      },
    },
  );
};

export const useAcceptSuggestedCheck = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useApiMutation<AcceptSuggestedCheckRequest, Check>(
    "POST",
    "/checks/suggested/{suggestion_id}/accept",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["/checks/suggested"] });
        queryClient.invalidateQueries({ queryKey: ["/checks"] });
        enqueueSnackbar("Check created successfully", {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to create check: ${error.message}`, {
          variant: "error",
        });
      },
    },
  );
};

export const useDismissSuggestedCheck = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useApiMutation<void, void>("POST", "/checks/suggested/{suggestion_id}/dismiss", {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/checks/suggested"] });
      enqueueSnackbar("Suggestion dismissed", {
        variant: "info",
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to dismiss suggestion: ${error.message}`, {
        variant: "error",
      });
    },
  });
};

export const useBatchAcceptSuggestedChecks = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useApiMutation<BatchSuggestedCheckRequest, BatchAcceptResponse>(
    "POST",
    "/checks/suggested/batch-accept",
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["/checks/suggested"] });
        queryClient.invalidateQueries({ queryKey: ["/checks"] });
        if (data.accepted > 0) {
          enqueueSnackbar(`${data.accepted} check(s) created successfully`, {
            variant: "success",
          });
        }
        if (data.errors.length > 0) {
          enqueueSnackbar(`${data.errors.length} suggestion(s) failed`, {
            variant: "warning",
          });
        }
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to accept suggestions: ${error.message}`, {
          variant: "error",
        });
      },
    },
  );
};

export const useBatchDismissSuggestedChecks = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useApiMutation<BatchSuggestedCheckRequest, BatchDismissResponse>(
    "POST",
    "/checks/suggested/batch-dismiss",
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["/checks/suggested"] });
        if (data.dismissed > 0) {
          enqueueSnackbar(`${data.dismissed} suggestion(s) dismissed`, {
            variant: "info",
          });
        }
        if (data.errors.length > 0) {
          enqueueSnackbar(`${data.errors.length} suggestion(s) failed`, {
            variant: "warning",
          });
        }
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to dismiss suggestions: ${error.message}`, {
          variant: "error",
        });
      },
    },
  );
};
